/**
 * Obtiene solicitudes aprobadas del usuario autenticado.
 * @param {string} token - Token JWT
 * @returns {Promise<Array>} Lista de solicitudes aprobadas
 */
export const getSolicitudesAprobadasUsuario = async (token) => {
  const res = await axios.get(`${BASE_URL}/solicitudes/aprobadas/mias`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data;
};
import axios from "axios";

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || "http://localhost:4000";
const BASE_URL = `${BACKEND_URL}/api/v1`;

export { BACKEND_URL };

// ── Interceptor de refresh automático ──
// Cola de peticiones que fallaron mientras se renovaba el token
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(token);
  });
  failedQueue = [];
};

// Interceptor de respuesta: si un 401, intentar renovar token automáticamente
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // No reintentar si ya es un retry, si es la propia petición de refresh, o si no es 401
    if (
      error.response?.status !== 401 ||
      originalRequest._retry ||
      originalRequest.url?.includes('/auth/refresh') ||
      originalRequest.url?.includes('/auth/login')
    ) {
      return Promise.reject(error);
    }

    if (isRefreshing) {
      // Otra petición ya está renovando: encolar esta
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then((token) => {
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return axios(originalRequest);
      });
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      const refreshToken = localStorage.getItem("refreshToken");
      if (!refreshToken) throw new Error("No refresh token");

      const { data } = await axios.post(`${BASE_URL}/auth/refresh`, { refreshToken });
      localStorage.setItem("token", data.token);

      processQueue(null, data.token);
      originalRequest.headers.Authorization = `Bearer ${data.token}`;
      return axios(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError, null);
      // Refresh falló: forzar logout
      localStorage.removeItem("token");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("user");
      window.location.href = "/login";
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);

/**
 * Autentica un usuario con email y contraseña.
 * @param {string} email - Email del usuario
 * @param {string} password - Contraseña
 * @returns {Promise<Object>} Usuario y token JWT
 */
export const loginUser = async (email, password) => {
  const res = await axios.post(`${BASE_URL}/auth/login`, { email, password });
  return res.data;
};

/**
 * Registra un fichaje de entrada, salida o ausencia.
 * @param {string} token - Token JWT
 * @param {string} tipo - Tipo de fichaje (entrada, salida, ausencia)
 * @returns {Promise<Object>} Fichaje creado
 */
export const fichar = async (token, tipo, opciones = {}) => {
  const body = { tipo };
  
  // SIEMPRE enviar clientTimestamp para que el backend pueda usarlo
  body.clientTimestamp = opciones.clientTimestamp || new Date().toISOString();
  
  // Añadir campos opcionales si existen
  if (opciones.timestamp) body.timestamp = opciones.timestamp;
  if (opciones.offline) body.offline = opciones.offline;
  if (opciones.uuid) body.uuid = opciones.uuid;
  
  const res = await axios.post(
    `${BASE_URL}/fichajes`,
    body,
    {
      headers: { Authorization: `Bearer ${token}` },
      timeout: opciones.timeout || 8000 // 8s timeout por defecto
    }
  );
  return res.data;
};

/**
 * Obtiene fichajes del trabajador que requieren validación (pendientes y rechazados).
 * @param {string} token - Token JWT
 * @returns {Promise<Array>} Lista de fichajes pendientes/rechazados
 */
export const getMisFichajesValidacion = async (token) => {
  const res = await axios.get(`${BASE_URL}/fichajes/mis-validaciones`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data;
};

/**
 * Obtiene fichajes con filtros opcionales.
 * @param {string} token - Token JWT
 * @param {string} [query] - Parámetros de consulta
 * @returns {Promise<Array>} Lista de fichajes
 */
export const getFichajes = async (token, query = "") => {
  const separator = query.includes('?') ? '&' : '?';
  const timestamp = new Date().getTime();
  const res = await axios.get(`${BASE_URL}/fichajes${query}${separator}_t=${timestamp}`, {
    headers: { 
      Authorization: `Bearer ${token}`,
      'Cache-Control': 'no-cache, no-store, must-revalidate',
      'Pragma': 'no-cache',
      'Expires': '0'
    },
  });
  return res.data;
};

export const getUsers = async (token) => {
  const timestamp = new Date().getTime();
  const res = await axios.get(`${BASE_URL}/users?_t=${timestamp}`, {
    headers: { 
      Authorization: `Bearer ${token}`,
      'Cache-Control': 'no-cache, no-store, must-revalidate',
      'Pragma': 'no-cache',
      'Expires': '0'
    },
  });
  return res.data?.data ?? res.data;
};

/**
 * Crea un nuevo usuario.
 * @param {string} token - Token JWT
 * @param {FormData} userData - Datos del usuario (form-data con imagen)
 * @returns {Promise<Object>} Usuario creado
 */
export const createUser = async (token,userData) => {
  const res = await axios.post(`${BASE_URL}/users`, userData, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return res.data;
};

/**
 * Actualiza un usuario existente.
 * @param {string} token - Token JWT
 * @param {string} id - ID del usuario
 * @param {FormData} formData - Datos a actualizar (form-data)
 * @returns {Promise<Object>} Usuario actualizado
 */
export const updateUser = async (token, id, formData) => {
  const res = await axios.put(`${BASE_URL}/users/${id}`, formData, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data;
};

export const deleteUser = async (token, id) => {
  const res = await axios.delete(`${BASE_URL}/users/${id}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data;
};

export const toggleUserEnabled = async (token, id, habilitado) => {
  const res = await axios.put(
    `${BASE_URL}/users/${id}/habilitar`,
    { habilitado },
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return res.data;
};

export const getEmpresas = async (token) => {
  const res = await axios.get(`${BASE_URL}/empresas`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data?.data ?? res.data;
};

// Crear empresa con imagen (FORM DATA)
export const crearEmpresa = async (token, formData) => {
  const res = await axios.post(`${BASE_URL}/empresas`, formData, {
    headers: {
      Authorization: `Bearer ${token}`,
      // "Content-Type": "multipart/form-data", // <-- remove this
    },
  });
  return res.data;
};

// Actualizar empresa (FORM DATA)
export const actualizarEmpresa = async (token, id, formData) => {
  const res = await axios.put(`${BASE_URL}/empresas/${id}`, formData, {
    headers: {
      Authorization: `Bearer ${token}`,
      // "Content-Type": "multipart/form-data", // <-- remove this
    },
  });
  return res.data;
};

// Eliminar empresa
export const eliminarEmpresa = async (token, id) => {
  return axios.delete(`${BASE_URL}/empresas/${id}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
};

// Toggle habilitado empresa
export const toggleEmpresaEnabled = async (token, id, habilitado) => {
  const res = await axios.put(
    `${BASE_URL}/empresas/${id}/habilitar`,
    { habilitado },
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return res.data;
};

// Exportar empresa (descarga JSON)
export const exportarEmpresa = async (token, id) => {
  const res = await axios.get(`${BASE_URL}/empresas/${id}/export`, {
    headers: { Authorization: `Bearer ${token}` },
    responseType: "blob",
  });
  // Crear descarga
  const url = window.URL.createObjectURL(new Blob([res.data]));
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", `empresa_${new Date().toISOString().split("T")[0]}.json`);
  document.body.appendChild(link);
  link.click();
  link.parentNode.removeChild(link);
  window.URL.revokeObjectURL(url);
};

// Importar empresa desde JSON
export const importarEmpresa = async (token, file) => {
  const formData = new FormData();
  formData.append("archivo", file);
  const res = await axios.post(`${BASE_URL}/empresas/import`, formData, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data;
};

// ---------------------- SOLICITUDES ----------------------

// Crear solicitud
export const createSolicitud = async (token, solicitudData) => {
  const res = await axios.post(`${BASE_URL}/solicitudes`, solicitudData, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data;
};

// Obtener solicitudes (filtradas por rol en backend)
export const getSolicitudes = async (token) => {
  const res = await axios.get(`${BASE_URL}/solicitudes`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data?.data ?? res.data;
};

// Actualizar estado de solicitud (aprobar/denegar)
export const updateEstadoSolicitud = async (token, id, estado, motivoRechazo = "") => {
  const res = await axios.put(
    `${BASE_URL}/solicitudes/${id}`,
    { estado, motivoRechazo },
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return res.data;
};

// Obtener solicitudes aprobadas (para control de horarios)
export const getSolicitudesAprobadas = async (token) => {
  const res = await axios.get(`${BASE_URL}/solicitudes?estado=aprobada`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data?.data ?? res.data;
};

// Eliminar solicitud
export const deleteSolicitud = async (token, id) => {
  const res = await axios.delete(`${BASE_URL}/solicitudes/${id}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data;
};

// ---------------------- MODIFICACIONES DE FICHAJES ----------------------
// Crear fichaje manual
export const crearFichajeManual = async (token, data) => {
  const res = await axios.post(
    `${BASE_URL}/modificaciones-fichajes/crear`,
    data,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return res.data;
};

// Editar fichaje existente
export const editarFichaje = async (token, fichajeId, data) => {
  const res = await axios.put(
    `${BASE_URL}/modificaciones-fichajes/editar/${fichajeId}`,
    data,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return res.data;
};

// Eliminar fichaje
export const eliminarFichaje = async (token, fichajeId, motivo) => {
  const res = await axios.delete(
    `${BASE_URL}/modificaciones-fichajes/eliminar/${fichajeId}`,
    { 
      headers: { Authorization: `Bearer ${token}` },
      data: { motivo }
    }
  );
  return res.data;
};

// Obtener historial de modificaciones de un empleado
export const getHistorialModificaciones = async (token, empleadoId, desde = null, hasta = null) => {
  let query = '';
  if (desde) query += `?desde=${desde}`;
  if (hasta) query += `${query ? '&' : '?'}hasta=${hasta}`;
  
  const res = await axios.get(
    `${BASE_URL}/modificaciones-fichajes/historial/${empleadoId}${query}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return res.data;
};

// Obtener todas las modificaciones (admin global)
export const getTodasModificaciones = async (token, filtros = {}) => {
  const params = new URLSearchParams();
  if (filtros.desde) params.append('desde', filtros.desde);
  if (filtros.hasta) params.append('hasta', filtros.hasta);
  if (filtros.empleadoId) params.append('empleadoId', filtros.empleadoId);
  if (filtros.adminId) params.append('adminId', filtros.adminId);
  
  const query = params.toString() ? `?${params.toString()}` : '';
  
  const res = await axios.get(
    `${BASE_URL}/modificaciones-fichajes/todas${query}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return res.data;
};
